import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const HistorialDevoluciones = () => {
  const [devoluciones, setDevoluciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    forma_devolucion: '',
    cliente: ''
  });
  const { token } = useAuth();
  const navigate = useNavigate();

  // Obtener todas las devoluciones
  const fetchDevoluciones = async () => {
    setLoading(true);
    setError('');

    try {
      let url = `${import.meta.env.VITE_API_URL}/devoluciones`;
      
      // Agregar filtros si están definidos
      const params = new URLSearchParams();
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);
      if (filtros.forma_devolucion) params.append('forma_devolucion', filtros.forma_devolucion);
      if (filtros.cliente) params.append('cliente', filtros.cliente);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { 'x-auth-token': token }
      });

      setDevoluciones(response.data);
    } catch (err) {
      console.error('Error al obtener devoluciones:', err);
      setError('Error al cargar el historial de devoluciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevoluciones();
  }, []);

  // Manejar cambios en filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    fetchDevoluciones();
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      fechaInicio: '',
      fechaFin: '',
      forma_devolucion: '',
      cliente: ''
    });
    // Recargar sin filtros
    setTimeout(() => fetchDevoluciones(), 100);
  };

  // Calcular totales
  const calcularTotales = () => {
    const total = devoluciones.reduce((sum, dev) => sum + parseFloat(dev.monto_devuelto || 0), 0);
    return {
      totalDevoluciones: devoluciones.length,
      montoTotal: total.toFixed(2)
    };
  };

  const totales = calcularTotales();

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          ← Volver
        </button>
        <h1 className="text-3xl font-bold">Historial de Devoluciones</h1>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Inicio</label>
            <input
              type="date"
              value={filtros.fechaInicio}
              onChange={(e) => handleFiltroChange('fechaInicio', e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Fin</label>
            <input
              type="date"
              value={filtros.fechaFin}
              onChange={(e) => handleFiltroChange('fechaFin', e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Forma de Devolución</label>
            <select
              value={filtros.forma_devolucion}
              onChange={(e) => handleFiltroChange('forma_devolucion', e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Todas</option>
              <option value="efectivo">Efectivo</option>
              <option value="cuenta_corriente">Cuenta Corriente</option>
              <option value="nota_credito">Nota de Crédito</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cliente</label>
            <input
              type="text"
              value={filtros.cliente}
              onChange={(e) => handleFiltroChange('cliente', e.target.value)}
              placeholder="Nombre del cliente"
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={aplicarFiltros}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Aplicar Filtros
          </button>
          <button
            onClick={limpiarFiltros}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold">Total Devoluciones</h3>
          <p className="text-2xl font-bold text-blue-600">{totales.totalDevoluciones}</p>
        </div>
        <div className="bg-red-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold">Monto Total Devuelto</h3>
          <p className="text-2xl font-bold text-red-600">${totales.montoTotal}</p>
        </div>
      </div>

      {/* Mostrar error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="bg-red-500 text-white p-4 rounded mb-4"
          >
            <div className="flex justify-between items-center">
              <p>{error}</p>
              <button onClick={() => setError('')} className="text-xl">×</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de devoluciones */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-lg">Cargando devoluciones...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h2 className="text-xl font-semibold p-6 border-b">Devoluciones Registradas</h2>
          
          {devoluciones.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>No se encontraron devoluciones con los filtros aplicados.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Venta</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forma</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {devoluciones.map((devolucion) => (
                    <motion.tr
                      key={devolucion.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {devolucion.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => navigate(`/ventas`)} // Podrías navegar a la venta específica
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          #{devolucion.id_venta}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {devolucion.producto?.nombre_producto || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {devolucion.cantidad_devuelta}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                        ${parseFloat(devolucion.monto_devuelto || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          devolucion.forma_devolucion === 'efectivo' ? 'bg-green-100 text-green-800' :
                          devolucion.forma_devolucion === 'cuenta_corriente' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {devolucion.forma_devolucion === 'efectivo' ? 'Efectivo' :
                           devolucion.forma_devolucion === 'cuenta_corriente' ? 'Cta. Corriente' :
                           'Nota Crédito'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {devolucion.motivo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(devolucion.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {devolucion.usuario?.nombre_user || 'N/A'}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Acciones adicionales */}
      <div className="mt-6 flex justify-between items-center">
        <button
          onClick={() => navigate('/devolucion')}
          className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Nueva Devolución
        </button>
        
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Imprimir Reporte
          </button>
          <button
            onClick={fetchDevoluciones}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Actualizar
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistorialDevoluciones;
